import { z } from 'zod';

/**
 * Mirrors the password schemas in the backend's `auth.validation.ts`.
 *
 * The same strength rule is written once here and reused by every screen that
 * sets a password, so "set password" and "change password" can never drift into
 * demanding different things.
 */
/** How long a password has to be. Named, because two things below read it. */
export const MIN_PASSWORD_LENGTH = 8;
/** Past this, a password is long enough to be worth more than its parts. */
export const STRONG_PASSWORD_LENGTH = 12;

/**
 * What makes a password acceptable, as a list rather than a chain.
 *
 * The schema below is built from this, and so is the checklist the reader
 * watches tick off as they type. One definition, so a rule can never be
 * enforced without being shown or shown without being enforced (CLAUDE.md §3) —
 * which is the failure that makes a form feel broken: a green tick beside a
 * field the server then refuses.
 *
 * `label` is what the reader is asked for; `message` is what they are told when
 * it is missing. They differ because one is an instruction and the other is a
 * complaint.
 */
export interface PasswordRule {
  key: string;
  label: string;
  message: string;
  test: (value: string) => boolean;
}

export const PASSWORD_RULES: readonly PasswordRule[] = [
  {
    key: 'length',
    label: `At least ${MIN_PASSWORD_LENGTH} characters`,
    message: `Use at least ${MIN_PASSWORD_LENGTH} characters`,
    test: (value) => value.length >= MIN_PASSWORD_LENGTH,
  },
  {
    key: 'lower',
    label: 'A lowercase letter',
    message: 'Include a lowercase letter',
    test: (value) => /[a-z]/.test(value),
  },
  {
    key: 'upper',
    label: 'An uppercase letter',
    message: 'Include an uppercase letter',
    test: (value) => /[A-Z]/.test(value),
  },
  {
    key: 'digit',
    label: 'A number',
    message: 'Include a number',
    test: (value) => /\d/.test(value),
  },
  {
    key: 'symbol',
    label: 'A special character (e.g. @ # $ !)',
    message: 'Include a special character',
    // Anything that is not a letter, a digit or a space — the server's rule.
    test: (value) => /[^\p{L}\p{N}\s]/u.test(value),
  },
];

/**
 * How far a password has got, from 0 to 6.
 *
 * The five rules, plus one for genuine length: a password that satisfies every
 * rule at exactly eight characters is acceptable, not strong, and a meter that
 * called it "Strong" would be lying to somebody about to reuse it everywhere.
 */
export function passwordScore(value: string): number {
  if (!value) return 0;
  const passed = PASSWORD_RULES.filter((rule) => rule.test(value)).length;
  return passed + (value.length >= STRONG_PASSWORD_LENGTH ? 1 : 0);
}

const strongPassword = z
  .string()
  .max(128, 'That is too long')
  // Every rule reported at once rather than the first that fails, so somebody
  // missing two of them is told both instead of discovering the second after
  // fixing the first.
  .superRefine((value, ctx) => {
    for (const rule of PASSWORD_RULES) {
      if (!rule.test(value)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: rule.message });
      }
    }
  });

export const forgotPasswordSchema = z.object({
  identifier: z
    .string()
    .trim()
    .min(1, 'Enter your email or phone number')
    .max(150, 'That is too long'),
});

export const setPasswordSchema = z
  .object({
    newPassword: strongPassword,
    confirmPassword: z.string().min(1, 'Type the password again'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Both passwords must match',
  });

export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Enter your current password'),
    newPassword: strongPassword,
    confirmPassword: z.string().min(1, 'Type the new password again'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Both passwords must match',
  })
  .refine((data) => data.currentPassword !== data.newPassword, {
    path: ['newPassword'],
    message: 'Choose a password different from your current one',
  });

export type ForgotPasswordValues = z.infer<typeof forgotPasswordSchema>;
export type SetPasswordValues = z.infer<typeof setPasswordSchema>;
export type ChangePasswordValues = z.infer<typeof changePasswordSchema>;
